/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React from 'react';
import { getPlaceholderText } from '@/utils/generalUtils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@radix-ui/react-tooltip';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface TreeDataItem {
  id: string;
  name: string;
  icon?: any;
  selectedIcon?: any;
  openIcon?: any;
  children?: TreeDataItem[];
  actions?: React.ReactNode;
  onClick?: () => void;
  draggable?: boolean;
  droppable?: boolean;
  disabled?: boolean;
}

interface TreeDropdownProps {
  data: TreeDataItem[];
  selectedItem: string | null;
  onSelect: (item: TreeDataItem) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const TreeDropdown: React.FC<TreeDropdownProps> = ({
  data,
  selectedItem,
  onSelect,
  placeholder = 'Select Filter',
  className,
  disabled,
}) => {
  const [open, setOpen] = React.useState(false);
  const [expandedItems, setExpandedItems] = React.useState<Set<string>>(
    new Set()
  );

  // Initialize expanded items when dropdown opens
  React.useEffect(() => {
    if (open && expandedItems.size === 0) {
      const firstLevelItems = new Set<string>();
      data.forEach((item) => {
        if (item.children && item.children.length > 0) {
          firstLevelItems.add(item.id);
        }
      });
      setExpandedItems(firstLevelItems);
    }
  }, [open, data, expandedItems.size]);

  const toggleExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const handleSelect = (item: TreeDataItem) => {
    onSelect(item);
    setOpen(false);
    item.onClick?.();
  };

  const renderTreeItem = (
    item: TreeDataItem,
    level: number = 0,
    isLast: boolean = false,
    parentLines: boolean[] = []
  ) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const isSelected = selectedItem === item.id;
    return (
      <div key={item.id}>
        <div
          className={cn(
            'relative flex cursor-pointer items-center py-2 text-sm hover:bg-[#EDEBE9]',
            isSelected && 'bg-[#EDEBE9]'
          )}
        >
          {/* Dotted lines */}
          {level > 0 && (
            <>
              {/* L-shaped connector for current item */}
              {Array.from({ length: level }).map(
                (_, idx) =>
                  (idx === level - 1 || parentLines[idx]) && (
                    <div
                      key={idx}
                      className="absolute border-l border-dotted border-[#C1C9CF]"
                      style={{
                        left: `${16 + idx * 24}px`,
                        top: 0,
                        height: '50%',
                        width: '1px',
                      }}
                    />
                  )
              )}
              {!hasChildren && (
                <div
                  className="absolute border-t border-dotted border-[#C1C9CF]"
                  style={{
                    left: `${16 + (level - 1) * 24}px`,
                    top: '50%',
                    width: '32px',
                    height: '1px',
                  }}
                />
              )}

              {/* Continue vertical line down if not last item */}
              {Array.from({ length: isLast ? level - 1 : level }).map(
                (_, idx) =>
                  parentLines[idx] && (
                    <div
                      key={idx}
                      className="absolute border-l border-dotted border-[#C1C9CF]"
                      style={{
                        left: `${16 + idx * 24}px`,
                        top: '50%',
                        bottom: 0,
                        width: '1px',
                      }}
                    />
                  )
              )}
            </>
          )}

          <div
            className="flex flex-1 items-center gap-2"
            style={{
              paddingLeft: `${level > 0 ? level * 24 + 8 : 8}px`,
            }}
          >
            <ChevronRight
              className={cn(
                'mr-1 size-4 shrink-0 text-[#0A2D3A] transition-transform duration-200',
                isExpanded && 'rotate-90',
                !hasChildren && 'opacity-0'
              )}
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(item.id);
              }}
            />

            {/* Icon */}
            {item.icon && <item.icon className="mr-2 h-4 w-4 shrink-0" />}

            <span
              className={cn(
                'flex-1 truncate',
                item.disabled && 'cursor-not-allowed opacity-50'
              )}
              onClick={() => {
                if (item.disabled) return;
                handleSelect(item);
              }}
            >
              {item.name}
            </span>

            {/* Actions */}
            {item.actions && isSelected && (
              <div className="ml-2">{item.actions}</div>
            )}
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div>
            {item.children!.map((child, index, array) => {
              const isChildLast = index === array.length - 1;
              const newParentLines = [...parentLines, !isChildLast];
              return renderTreeItem(
                child,
                level + 1,
                isChildLast,
                newParentLines
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const handleToggleDropdown = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleToggleDropdown}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between border border-line-primary bg-transparent p-1 text-[#8C9EA1] hover:bg-transparent hover:text-[#8C9EA1]',
            className
          )}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="mx-3 block  truncate "
                  style={{ direction: 'rtl' }}
                >
                  <span
                    style={{ direction: 'ltr', unicodeBidi: 'plaintext' }}
                    className={cn(disabled ? 'opacity-50' : 'opacity-100')}
                  >
                    {getPlaceholderText(selectedItem, data, placeholder)}
                  </span>
                </span>
              </TooltipTrigger>
              <TooltipContent
                className="rounded bg-black p-[9px] text-[13px] leading-4 tracking-[2%] text-white"
                sideOffset={15}
              >
                {getPlaceholderText(selectedItem, data, placeholder)}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <ChevronDown className={cn('mx-2 size-4 text-body-secondary')} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-full p-0"
        style={{ width: 'var(--radix-dropdown-menu-trigger-width)' }}
      >
        <div className="max-h-[300px] overflow-y-auto rounded-none bg-white text-black">
          {data.map((item, index, array) =>
            renderTreeItem(item, 0, index === array.length - 1, [])
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { TreeDropdown, type TreeDataItem };
